/**
 * Modal para crear Club
 * Club unificado con secciones activables (Aventureros, Conquistadores, Guías Mayores)
 * Iglesia es opcional
 */

import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button, Input } from "@/components/ui";
import { X, Loader2, Building2 } from "lucide-react";
import {
  useCreateClub,
  useChurchesByAssociation,
} from "@/hooks/useOrganization";
import { useAuthStore } from "@/stores/authStore";

const clubSchema = z.object({
  name: z.string().min(3, "El nombre debe tener al menos 3 caracteres"),
  churchId: z.string().optional(),
  sectionAventureros: z.boolean().optional(),
  sectionConquistadores: z.boolean().optional(),
  sectionGuiasMayores: z.boolean().optional(),
  meetingDay: z.string().optional(),
  meetingTime: z.string().optional(),
  contactEmail: z.string().email("Email inválido").optional().or(z.literal("")),
  contactPhone: z.string().optional(),
});

type ClubForm = z.infer<typeof clubSchema>;

interface CreateClubModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function CreateClubModal({
  isOpen,
  onClose,
}: CreateClubModalProps) {
  const { profile } = useAuthStore();
  const associationId = profile?.scopeId;

  const { data: churches, isLoading: loadingChurches } =
    useChurchesByAssociation(associationId);
  const createMutation = useCreateClub();

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<ClubForm>({
    resolver: zodResolver(clubSchema),
    defaultValues: {
      name: "",
      churchId: "",
      sectionConquistadores: true,
      sectionAventureros: false,
      // Guías Mayores viene abierto a propósito: TODO mayor de 16 (directiva,
      // consejeros, el propio director) se archiva en ese tipo al entrar al
      // club. Si nace cerrado, esa gente queda dentro de un tipo cerrado y no
      // se ve — así terminaron 557 personas escondidas en 49 clubes.
      sectionGuiasMayores: true,
      meetingDay: "",
      meetingTime: "",
      contactEmail: "",
      contactPhone: "",
    },
  });

  const hasAnySection =
    watch("sectionAventureros") ||
    watch("sectionConquistadores") ||
    watch("sectionGuiasMayores");

  const onSubmit = async (data: ClubForm) => {
    if (!hasAnySection) return;

    try {
      const selectedChurch = data.churchId
        ? churches?.find((c) => c.id === data.churchId)
        : null;

      const clubData: Record<string, unknown> = {
        name: data.name,
        // Legacy: usar la primera sección activa como type para compatibilidad
        type: data.sectionConquistadores
          ? "conquistadores"
          : data.sectionAventureros
            ? "aventureros"
            : "guias_mayores",
        sections: {
          aventureros: { active: !!data.sectionAventureros },
          conquistadores: { active: !!data.sectionConquistadores },
          guias_mayores: { active: !!data.sectionGuiasMayores },
        },
        associationId: associationId || selectedChurch?.associationId || "",
        status: "active",
        activeCount: 0,
        unitCount: 0,
      };

      if (data.churchId) {
        clubData.churchId = data.churchId;
        clubData.churchName = selectedChurch?.name || "";
      }

      if (data.meetingDay) clubData.meetingDay = data.meetingDay;
      if (data.meetingTime) clubData.meetingTime = data.meetingTime;
      if (data.contactEmail) clubData.contactEmail = data.contactEmail;
      if (data.contactPhone) clubData.contactPhone = data.contactPhone;

      await createMutation.mutateAsync(clubData as any);
      reset();
      onClose();
    } catch (error) {
      console.error("Error creating club:", error);
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
      />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto animate-in fade-in zoom-in-95 duration-200">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center shadow-md shadow-blue-500/25">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-lg font-semibold">Nuevo Club</h2>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-xl transition-colors duration-200"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {loadingChurches ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <Input
              label="Nombre del Club *"
              {...register("name")}
              error={errors.name?.message}
              placeholder="Ej: Orión, Centinelas, Águilas, etc."
            />

            {/* Secciones */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Secciones activas *
              </label>
              <div className="space-y-2">
                <label className="flex items-center gap-3 p-3 border-2 rounded-xl cursor-pointer hover:bg-gray-50 transition-colors">
                  <input
                    type="checkbox"
                    {...register("sectionAventureros")}
                    className="w-4 h-4 text-amber-500 rounded focus:ring-amber-500"
                  />
                  <div>
                    <span className="font-medium text-gray-800">
                      Aventureros
                    </span>
                    <span className="text-xs text-gray-500 ml-2">
                      (6-9 años)
                    </span>
                  </div>
                </label>
                <label className="flex items-center gap-3 p-3 border-2 rounded-xl cursor-pointer hover:bg-gray-50 transition-colors">
                  <input
                    type="checkbox"
                    {...register("sectionConquistadores")}
                    className="w-4 h-4 text-red-500 rounded focus:ring-red-500"
                  />
                  <div>
                    <span className="font-medium text-gray-800">
                      Conquistadores
                    </span>
                    <span className="text-xs text-gray-500 ml-2">
                      (10-15 años)
                    </span>
                  </div>
                </label>
                <label className="flex items-center gap-3 p-3 border-2 rounded-xl cursor-pointer hover:bg-gray-50 transition-colors">
                  <input
                    type="checkbox"
                    {...register("sectionGuiasMayores")}
                    className="w-4 h-4 text-green-500 rounded focus:ring-green-500"
                  />
                  <div>
                    <span className="font-medium text-gray-800">
                      Guías Mayores
                    </span>
                    <span className="text-xs text-gray-500 ml-2">
                      (16+ años)
                    </span>
                    <p className="text-xs text-amber-700 mt-0.5">
                      Déjalo abierto: aquí quedan la directiva y los consejeros
                      del club. Si lo cierras, no los verás.
                    </p>
                  </div>
                </label>
              </div>
              {!hasAnySection && (
                <p className="text-sm text-red-500 mt-1">
                  Debes activar al menos una sección
                </p>
              )}
            </div>

            {/* Iglesia (opcional) */}
            {churches && churches.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Iglesia <span className="text-gray-400">(opcional)</span>
                </label>
                <select
                  {...register("churchId")}
                  className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-primary-500/20 focus:border-primary-500 transition-all duration-300"
                >
                  <option value="">Sin iglesia asignada</option>
                  {churches.map((church) => (
                    <option key={church.id} value={church.id}>
                      {church.name} {church.code ? `(${church.code})` : ""}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Día de reunión
                </label>
                <select
                  {...register("meetingDay")}
                  className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-primary-500/20 focus:border-primary-500 transition-all duration-300"
                >
                  <option value="">Seleccionar</option>
                  <option value="domingo">Domingo</option>
                  <option value="lunes">Lunes</option>
                  <option value="martes">Martes</option>
                  <option value="miercoles">Miércoles</option>
                  <option value="jueves">Jueves</option>
                  <option value="viernes">Viernes</option>
                  <option value="sabado">Sábado</option>
                </select>
              </div>

              <Input
                label="Hora de reunión"
                type="time"
                {...register("meetingTime")}
                error={errors.meetingTime?.message}
              />
            </div>

            <Input
              label="Email de contacto"
              type="email"
              {...register("contactEmail")}
              error={errors.contactEmail?.message}
              placeholder="club@email.com"
            />

            <Input
              label="Teléfono de contacto"
              {...register("contactPhone")}
              error={errors.contactPhone?.message}
              placeholder="+57 300 123 4567"
            />

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="secondary"
                onClick={handleClose}
                className="flex-1 rounded-xl"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={createMutation.isPending || !hasAnySection}
                className="flex-1 rounded-xl bg-gradient-to-r from-blue-500 to-blue-600 shadow-lg shadow-blue-500/25 hover:shadow-xl transition-all duration-300"
              >
                {createMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  "Crear Club"
                )}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
